import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { BASE_URL } from "../API/Constants";
import { guestCart } from "../components/Products";

const safe = (v, fb = "") => (v === null || v === undefined ? fb : String(v));
const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return safe(v, "-");
  return new Intl.NumberFormat("en-IN").format(n);
};

export default function AllCategories() {
  const nav = useNavigate();

  const [tab, setTab] = useState("TOP"); // TOP / BEAUTY / WELLNESS

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catErr, setCatErr] = useState("");

  const [activeCat, setActiveCat] = useState(null);

  const [subcats, setSubcats] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subErr, setSubErr] = useState("");

  const [activeSub, setActiveSub] = useState(null);

  const [products, setProducts] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodErr, setProdErr] = useState("");

  const [cartMap, setCartMap] = useState({});
  const [busyKey, setBusyKey] = useState("");

  const subRef = useRef(null);
  const prodRef = useRef(null);

  // Scroll to subcategories when activeCat changes
  useEffect(() => {
    if (activeCat && subRef.current) {
      setTimeout(() => {
        subRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [activeCat]);

  // Scroll to products when activeSub changes
  useEffect(() => {
    if (activeSub && prodRef.current) {
      setTimeout(() => {
        prodRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [activeSub]);

  // ✅ LOAD CART
  const loadCart = async () => {
    try {
      const res = await axiosInstance.get("/api/cart");
      const d = res.data || {};
      const items = (d.CartItems || d.cartItems || d.items || []) ?? [];

      const next = {};
      for (const it of items) {
        const prod = it?.product || it?.Product || it?.productDetails || {};
        const pid = prod?.id ?? it?.productId ?? it?.ProductId ?? it?.product_id;
        const cartItemId = it?.id ?? it?.cartItemId ?? it?._id;
        const qty = Number(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1);

        if (pid != null) next[String(pid)] = { cartItemId, qty };
      }
      setCartMap(next);
      // ✅ Also broadcast to Navbar
      const total = Object.values(next).reduce((s, r) => s + (Number(r?.qty) || 0), 0);
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: total } }));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      loadCart();
    } else {
      const raw = guestCart.get();
      setCartMap(guestCart.toCartMap(raw));
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: guestCart.totalCount(raw) } }));
    }
  }, []);

  const addToCart = async (e, p) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!p?.id) return;

    const token = localStorage.getItem("token");
    if (!token) {
      const updated = guestCart.add(p.id, p);
      const map = guestCart.toCartMap(updated);
      setCartMap(map);
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: guestCart.totalCount(updated) } }));
      return;
    }

    try {
      setBusyKey(`add:${p.id}`);
      await axiosInstance.post("/api/cart", { productId: p.id });
      await loadCart();
    } catch (er) {
      console.error(er);
    } finally {
      setBusyKey("");
    }
  };

  const updateCartQty = async (e, p, newQty) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!p?.id) return;

    const pid = String(p.id);
    const row = cartMap[pid];

    const token = localStorage.getItem("token");
    if (!token) {
      const updated = guestCart.updateQty(p.id, newQty);
      const map = guestCart.toCartMap(updated);
      setCartMap(map);
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: guestCart.totalCount(updated) } }));
      return;
    }

    if (!row?.cartItemId) {
      if (newQty >= 1) return addToCart(e, p);
      return;
    }
    if (newQty < 1) {
      try {
        setBusyKey(`qty:${p.id}`);
        await axiosInstance.delete(`/api/cart/${row.cartItemId}`);
        await loadCart();
      } catch (er) { console.error(er); }
      finally { setBusyKey(""); }
      return;
    }

    try {
      setBusyKey(`qty:${p.id}`);
      await axiosInstance.put(`/api/cart/${row.cartItemId}`, { qty: newQty });

      setCartMap((prev) => ({
        ...prev,
        [pid]: { ...prev[pid], qty: newQty },
      }));
      loadCart();
    } catch (er) {
      console.error(er);
      await loadCart();
    } finally {
      setBusyKey("");
    }
  };

  const renderStars = () => {
    return (
      <div className="acStars">
        {[1, 2, 3, 4].map(i => (
          <svg key={i} className="star-filled" viewBox="0 0 24 24" width="12" height="12">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        ))}
        <svg className="star-empty" viewBox="0 0 24 24" width="12" height="12">
          <path d="M22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
        </svg>
      </div>
    );
  };

  // ✅ IMPORTANT: IMAGE ORIGIN MUST NOT HAVE /api
  const IMAGE_ORIGIN = useMemo(() => {
    const b = (BASE_URL || "").trim().replace(/\/+$/, "");
    return b.replace(/\/api$/i, "");
  }, []);

  const imgUrl = (path) => {
    if (!path) return "";
    const p = String(path).trim();
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${IMAGE_ORIGIN}${p}`;
    return `${IMAGE_ORIGIN}/${p}`;
  };

  const pickList = (data, keys = []) => {
    if (Array.isArray(data)) return data;
    for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
    return [];
  };

  // ------------------ LOAD CATEGORIES ------------------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setCatLoading(true);
        setCatErr("");
        const res = await axiosInstance.get("/api/categories");
        const list = pickList(res.data, ["categories", "data", "items"]);

        const sorted = [...(list || [])].sort((a, b) => {
          const ao = Number.isFinite(+a?.sortOrder) ? +a.sortOrder : 999999;
          const bo = Number.isFinite(+b?.sortOrder) ? +b.sortOrder : 999999;
          if (ao !== bo) return ao - bo;

          const an = safe(a?.name, "").toLowerCase();
          const bn = safe(b?.name, "").toLowerCase();
          if (an < bn) return -1;
          if (an > bn) return 1;

          return (+a?.id || 0) - (+b?.id || 0);
        });

        if (!mounted) return;
        setCategories(sorted);
      } catch (e) {
        if (!mounted) return;
        setCatErr(
          e?.response?.data?.msg ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load categories"
        );
      } finally {
        if (mounted) setCatLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ------------------ TAB FILTER ------------------
  const filteredCategories = useMemo(() => {
    const t = tab;

    return categories.filter((c) => {
      const grp = safe(c?.group, "").toUpperCase();
      if (grp) return grp === t;

      const nm = safe(c?.name, "").toLowerCase();
      if (t === "TOP") return true;
      if (t === "BEAUTY") return nm.includes("beauty") || nm.includes("skin") || nm.includes("hair");
      if (t === "WELLNESS") return nm.includes("wellness") || nm.includes("fitness") || nm.includes("health");
      return true;
    });
  }, [categories, tab]);

  // ------------------ CLICK CATEGORY -> LOAD SUBCATEGORIES ------------------
  const openCategory = async (cat) => {
    setActiveCat(cat);
    setActiveSub(null);
    setProducts([]);

    try {
      setSubLoading(true);
      setSubErr("");
      setSubcats([]);

      const res = await axiosInstance.get(`/api/subcategories?categoryId=${cat.id}`);
      const list = pickList(res.data, ["subcategories", "data", "items"]);
      setSubcats(list || []);
    } catch (e) {
      setSubErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load subcategories"
      );
    } finally {
      setSubLoading(false);
    }
  };

  // ------------------ CLICK SUBCATEGORY -> LOAD PRODUCTS ------------------
  const openSubcategory = async (sub) => {
    setActiveSub(sub);

    try {
      setProdLoading(true);
      setProdErr("");
      setProducts([]);

      const res = await axiosInstance.get(
        `/api/products?categoryId=${activeCat?.id || ""}&subCategoryId=${sub?.id || ""}`
      );
      const list = pickList(res.data, ["products", "data", "items"]);
      setProducts(list || []);
    } catch (e) {
      setProdErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load products"
      );
    } finally {
      setProdLoading(false);
    }
  };

  const getProductImage = (p) => {
    const img =
      Array.isArray(p?.images)
        ? p.images?.[0]
        : typeof p?.images === "string"
          ? (() => {
            try {
              const arr = JSON.parse(p.images);
              return arr?.[0] || "";
            } catch {
              return p.images;
            }
          })()
          : p?.image || "";
    return imgUrl(img);
  };

  return (
    <div className="acPage">
      <style>{css}</style>



      {/* BREADCRUMB */}
      <div className="acBreadcrumb">
        <span className="acCrumb" onClick={() => nav("/")}>
          Home
        </span>
        <span className="acSep">›</span>
        <span className="acCrumbActive">All Categories</span>
      </div>

      {/* TABS */}
      <div className="acTabs">
        <button className={tab === "TOP" ? "acTab acTabOn" : "acTab"} onClick={() => setTab("TOP")}>
          Top Categories
        </button>

      </div>

      {/* CATEGORIES (3 per row) */}
      <div className="acSection">
        {catErr ? <div className="acMsg acErr">{catErr}</div> : null}

        {catLoading ? (
          <div className="acGrid">
            {Array.from({ length: 9 }).map((_, i) => (
              <div className="acCard acSkel" key={i} />
            ))}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="acMsg">No categories found</div>
        ) : (
          <div className="acGrid">
            {filteredCategories.map((c) => (
              <button
                key={c?.id}
                className={activeCat?.id === c?.id ? "acCard acCardOn" : "acCard"}
                onClick={() => openCategory(c)}
                type="button"
                title={safe(c?.name, "")}
              >
                <div className="acImgBox">
                  <img
                    src={imgUrl(c?.image) || "https://via.placeholder.com/200?text=C"}
                    alt={safe(c?.name, "")}
                    loading="lazy"
                    onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/200?text=C")}
                  />
                </div>
                <div className="acName">{safe(c?.name, "")}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* SUBCATEGORIES */}
      {activeCat ? (
        <div className="acSection acBelow" ref={subRef}>
          <div className="acSubHead">
            <div className="acSubTitle">{safe(activeCat?.name)} • Subcategories</div>
            <button
              className="acClear"
              onClick={() => {
                setActiveCat(null);
                setActiveSub(null);
                setSubcats([]);
                setProducts([]);
              }}
            >
              Clear
            </button>
          </div>

          {subErr ? <div className="acMsg acErr">{subErr}</div> : null}

          {subLoading ? (
            <div className="acGridSub">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="acSubCard acSkel2" key={i} />
              ))}
            </div>
          ) : subcats.length === 0 ? (
            <div className="acMsg">No subcategories found</div>
          ) : (
            <div className="acGridSub">
              {subcats.map((s) => (
                <button
                  key={s?.id}
                  className={activeSub?.id === s?.id ? "acSubCard acSubOn" : "acSubCard"}
                  onClick={() => openSubcategory(s)}
                  type="button"
                  title={safe(s?.name)}
                >
                  <div className="acSubImg">
                    <img
                      src={imgUrl(s?.image) || "https://via.placeholder.com/200?text=S"}
                      alt={safe(s?.name)}
                      loading="lazy"
                      onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/200?text=S")}
                    />
                  </div>
                  <div className="acSubName">{safe(s?.name)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* PRODUCTS (2 per row, clean no color around) */}
      {activeCat && activeSub ? (
        <div className="acSection acBelow" ref={prodRef}>
          <div className="acSubHead">
            <div className="acSubTitle">{safe(activeSub?.name)} • Products</div>
          </div>

          {prodErr ? <div className="acMsg acErr">{prodErr}</div> : null}

          {prodLoading ? (
            <div className="acProdGrid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="acProdCard acSkel3" key={i} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="acMsg">No products found</div>
          ) : (
            <div className="acProdGrid">
              {products.map((p) => (
                <div
                  key={p?.id}
                  className="acProdCard"
                  onClick={() => nav(`/ProductDetails/${p.id}`)}
                  title={safe(p?.name)}
                >
                  <div className="acProdImg">
                    <img
                      src={getProductImage(p) || "https://via.placeholder.com/300?text=P"}
                      alt={safe(p?.name)}
                      loading="lazy"
                      onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/300?text=P")}
                    />
                  </div>
 
                   <div className="acProdInfo">
                     <div className="acProdName">{safe(p?.name)}</div>
                     
                     {renderStars()}
 
                     <div className="acProdMeta">
                       <span className="acPrice">₹{money(p?.price)}</span>
                       {p?.mrp ? <span className="acMrp">₹{money(p?.mrp)}</span> : null}
                     </div>
 
                     <div className="acProdBottom">
                       {Number(p?.stockQty) <= 0 ? (
                         <div className="acOut">Out of stock</div>
                       ) : (
                         <div className="acIn">In stock</div>
                       )}
 
                       {/* ✅ CART ACTIONS */}
                       <div className="acProdActions">
                         {Number(p?.stockQty) > 0 && (
                           <>
                             {Number(cartMap?.[String(p.id)]?.qty || 0) <= 0 ? (
                               <button className="acAddBtn" type="button" onClick={(e) => addToCart(e, p)} disabled={busyKey === `add:${p.id}`}>
                                 {busyKey === `add:${p.id}` ? "..." : "ADD"}
                               </button>
                             ) : (
                               <div className="acQtyBox" onClick={(e) => e.stopPropagation()}>
                                 <button type="button" className="acQtyBtn" disabled={busyKey === `qty:${p.id}`} onClick={(e) => updateCartQty(e, p, Number(cartMap?.[String(p.id)]?.qty || 0) - 1)}>−</button>
                                 <div className="acQtyVal">{cartMap?.[String(p.id)]?.qty}</div>
                                 <button type="button" className="acQtyBtn" disabled={busyKey === `qty:${p.id}`} onClick={(e) => updateCartQty(e, p, Number(cartMap?.[String(p.id)]?.qty || 0) + 1)}>+</button>
                               </div>
                             )}
                           </>
                         )}
                       </div>
                     </div>
                   </div>
                 </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div className="acBottomSpace" />
    </div>
  );
}

const css = `
  *{ box-sizing:border-box; }
  body{ margin:0; background:#fff; font-family: ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto; color:#0f172a; }

  :root{
    --teal:#006600;
    --card:#ffffff;
    --border: rgba(15,23,42,.12);
    --muted: rgba(15,23,42,.62);
    --shadow: 0 10px 22px rgba(0,0,0,.08);
  }

  .acPage{ width:100%; min-height:100vh; background:#fff; }

  /* TOP BAR */
  .acTopBar{
    position: sticky; top:0; z-index: 20;
    height: 56px;
    background: var(--teal);
    display:flex; align-items:center; justify-content:space-between;
    padding: 0 10px;
    box-shadow: 0 8px 18px rgba(0,0,0,.10);
  }
  .acBack,.acSearch{
    width: 40px; height: 40px;
    border:none; background: rgba(255,255,255,.18);
    color:#fff; border-radius: 12px;
    font-size: 18px; font-weight: 900;
    display:flex; align-items:center; justify-content:center;
    cursor:pointer;
  }
  .acTitle{
    color:#fff;
    font-size: 18px;
    font-weight: 900;
    letter-spacing: .2px;
  }

  /* BREADCRUMB */
  .acBreadcrumb{
    padding: 10px 14px;
    font-size: 13px;
    color: var(--muted);
    display:flex; gap: 8px; align-items:center;
  }
  .acCrumb{ cursor:pointer; font-weight: 800; }
  .acCrumbActive{ font-weight: 950; color:#0f172a; }
  .acSep{ opacity:.7; }

  /* TABS */
  .acTabs{
    display:flex;
    gap: 10px;
    padding: 6px 14px 2px;
    overflow-x:auto;
    -webkit-overflow-scrolling: touch;
  }
  .acTabs::-webkit-scrollbar{ height: 0; }
  .acTab{
    border: 1px solid rgba(15,23,42,.12);
    background: #fff;
    color:#0f172a;
    padding: 10px 14px;
    border-radius: 999px;
    font-weight: 950;
    cursor:pointer;
    white-space: nowrap;
  }
  .acTabOn{
    background: rgba(25,198,184,.14);
    border-color: rgba(25,198,184,.40);
  }

  /* SECTION */
  .acSection{ padding: 10px 14px; }
  .acBelow{ padding-top: 6px; }

  .acMsg{
    margin: 10px 0;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid rgba(15,23,42,.10);
    background:#fff;
    font-weight: 900;
    color: rgba(15,23,42,.72);
  }
  .acErr{
    border-color: rgba(239,68,68,.25);
    background: rgba(239,68,68,.06);
    color:#7f1d1d;
  }

  /* ✅ CATEGORIES GRID: 3 per row */
  .acGrid{
    display:grid;
    grid-template-columns: repeat(3, minmax(0,1fr));
    gap: 12px;
  }
  .acCard{
    border:none;
    background: transparent;
    cursor:pointer;
    text-align:center;
    padding: 0;
  }
  .acCardOn .acImgBox{ outline: 3px solid rgba(25,198,184,.28); }
  .acImgBox{
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 16px;
    background: #ffffff;               /* ✅ removed pink */
    border: 1px solid rgba(15,23,42,.08);
    box-shadow: 0 10px 18px rgba(0,0,0,.06);
    overflow:hidden;
    display:flex;
    align-items:center;
    justify-content:center;
  }
  .acImgBox img{
    width: 78%;
    height: 78%;
    object-fit: contain;
    display:block;
  }
  .acName{
    margin-top: 8px;
    font-size: 11px;
    font-weight: 400;
    color:#0f172a;
    line-height: 1.15;
  }

  /* SUB HEAD */
  .acSubHead{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: 10px;
    margin: 8px 0 10px;
  }
  .acSubTitle{
    font-size: 15px;
    font-weight: 950;
    color:#0f172a;
  }
  .acClear{
    border: 1px solid rgba(15,23,42,.12);
    background:#fff;
    padding: 8px 12px;
    border-radius: 999px;
    font-weight: 950;
    cursor:pointer;
  }

  /* SUBCATEGORIES GRID */
  .acGridSub{
    display:grid;
    grid-template-columns: repeat(3, minmax(0,1fr));
    gap: 12px;
  }
  .acSubCard{
    border: 1px solid rgba(15,23,42,.10);
    background:#fff;
    border-radius: 16px;
    padding: 10px;
    box-shadow: 0 10px 18px rgba(0,0,0,.06);
    cursor:pointer;
    text-align:left;
  }
  .acSubOn{ outline: 3px solid rgba(25,198,184,.22); }
  .acSubImg{
    width: 100%;
    aspect-ratio: 4 / 3;
    background: rgba(2,6,23,.03);
    border-radius: 12px;
    overflow:hidden;
    display:flex; align-items:center; justify-content:center;
  }
  .acSubImg img{ width:100%; height:100%; object-fit: cover; display:block; }
  .acSubName{
    margin-top: 8px;
    font-size: 13px;
    font-weight: 950;
    color:#0f172a;
    line-height: 1.2;
  }

  /* ✅ PRODUCTS GRID: 2 per row (mobile) */
  .acProdGrid{
    display:grid;
    grid-template-columns: repeat(2, minmax(0,1fr));
    gap: 12px;
  }
  .acProdCard{
    border: 1px solid rgba(15,23,42,.10);
    background:#fff;
    border-radius: 16px;
    padding: 10px;
    box-shadow: 0 10px 18px rgba(0,0,0,.06);
    cursor:pointer;
    text-align:left;
  }
  .acProdImg{
    width: 100%;
    aspect-ratio: 1 / 1;
    background: #ffffff;               /* ✅ removed color around image */
    border: 1px solid rgba(15,23,42,.06);
    border-radius: 14px;
    overflow:hidden;
    display:flex; align-items:center; justify-content:center;
  }
  .acProdImg img{ width:100%; height:100%; object-fit: cover; display:block; }

  .acProdInfo{ padding-top: 8px; }
  .acProdName{
    font-size: 13.5px;
    font-weight: 950;
    color:#0f172a;
    line-height: 1.2;
    display:-webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow:hidden;
    min-height: 34px;
  }
  .acProdMeta{
    display:flex;
    gap: 8px;
    align-items:baseline;
    margin-top: 6px;
  }
  .acPrice{ font-weight: 950; color:#0f172a; }
  .acMrp{ color: rgba(15,23,42,.55); text-decoration: line-through; font-weight: 900; font-size: 12px; }
  .acIn{ font-weight: 950; font-size: 11px; color: #166534; }
  .acOut{ font-weight: 950; font-size: 11px; color: #991b1b; }

  .acStars{ display: flex; gap: 2px; margin-top: 4px; }
  .star-filled{ fill: #f39c12; }
  .star-empty{ fill: rgba(0,0,0,.15); }

  .acProdBottom{ margin-top: 10px; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .acProdActions{ flex: 1; display: flex; justify-content: flex-end; }
  .acAddBtn{
    background: #006600; color: #fff; border: none; padding: 6px 12px; border-radius: 8px;
    font-weight: 800; font-size: 11px; cursor: pointer;
  }
  .acQtyBox{
    display: flex; align-items: center; background: #f0fdf2; border: 1px solid #006600;
    border-radius: 8px; overflow: hidden;
  }
  .acQtyBtn{
    width: 28px; height: 28px; border: none; background: transparent; color: #006600;
    font-weight: 800; cursor: pointer;
  }
  .acQtyVal{ padding: 0 4px; font-weight: 800; font-size: 12px; color: #006600; }

  /* SKELETONS */
  .acSkel{
    height: 0;
    padding-bottom: 120%;
    border-radius: 16px;
    background: linear-gradient(90deg, rgba(0,0,0,.04), rgba(0,0,0,.08), rgba(0,0,0,.04));
    background-size: 240% 100%;
    animation: shimmer 1.2s infinite;
  }
  .acSkel2,.acSkel3{
    height: 150px;
    border-radius: 16px;
    background: linear-gradient(90deg, rgba(0,0,0,.04), rgba(0,0,0,.08), rgba(0,0,0,.04));
    background-size: 240% 100%;
    animation: shimmer 1.2s infinite;
  }
  @keyframes shimmer{
    0%{ background-position: 0% 0; }
    100%{ background-position: 140% 0; }
  }

  /* RESPONSIVE */
  @media (max-width: 420px){
    .acGrid{ gap: 10px; }
    .acName{ font-size: 12.5px; }
    .acGridSub{ grid-template-columns: repeat(2, minmax(0,1fr)); }
  }
  @media (min-width: 900px){
    .acSection{ max-width: 1100px; margin: 0 auto; }
    .acProdGrid{ grid-template-columns: repeat(4, minmax(0,1fr)); } /* desktop more columns */
    .acGridSub{ grid-template-columns: repeat(5, minmax(0,1fr)); }
    .acGrid{ grid-template-columns: repeat(6, minmax(0,1fr)); }     /* desktop categories more */
  }

  .acBottomSpace{ height: 24px; }
`;








